import { useCallback } from 'react';
import { apiRequest } from '../services/apiClient';
import { API_BASE_URL } from '../config/api';

/**
 * Mutation client for /api/employees document endpoints. The upload
 * path goes direct to fetch() so the FormData body propagates
 * correctly (apiRequest assumes JSON). Archive + signed-url use the
 * standard JSON apiRequest path.
 */
export function useEmployeeDocumentMutations({ profileId } = {}) {
  const uploadDocument = useCallback(async ({ file, documentType, metadata, expiresAt } = {}) => {
    if (!profileId) throw new Error('profileId required for upload.');
    const body = new FormData();
    body.append('document', file);
    if (documentType) body.append('documentType', documentType);
    if (metadata && Object.keys(metadata).length) {
      body.append('metadata', JSON.stringify(metadata));
    }
    if (expiresAt) body.append('expiresAt', expiresAt);
    const response = await fetch(`${API_BASE_URL}/api/employees/profiles/${profileId}/documents`, {
      method: 'POST',
      credentials: 'include',
      body
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok || payload.success === false) {
      const error = new Error(payload.error || `Document upload failed (${response.status}).`);
      error.status = response.status;
      error.payload = payload;
      throw error;
    }
    return payload.data;
  }, [profileId]);

  const archiveDocument = useCallback((documentId) => (
    apiRequest(`/api/employees/documents/${documentId}/archive`, {
      method: 'POST',
      body: JSON.stringify({})
    })
  ), []);

  const getSignedUrl = useCallback((documentId) => (
    apiRequest(`/api/employees/documents/${documentId}/signed-url`)
  ), []);

  // Module 2 Commit 6 — inline expiration editing. Payload is the
  // server-side XOR: { expiresAt } | { clear: true }.
  const setExpiration = useCallback((documentId, payload) => (
    apiRequest(`/api/employees/documents/${documentId}/expiration`, {
      method: 'PATCH',
      body: JSON.stringify(payload)
    })
  ), []);

  return { uploadDocument, archiveDocument, getSignedUrl, setExpiration };
}
